import apiClient from "@/services/api";
import { API_ROOT } from "@/config";
import keycloakService from "@/services/keycloak";

/**
 * Il canale con cui il server ci avverte che e' successo qualcosa.
 *
 * Uno solo per tutta l'applicazione: messaggi, prenotazioni, check-in, notifiche. Le
 * schermate si iscrivono ai tipi che le riguardano e ricaricano solo quel pezzo, invece di
 * chiedere ogni trenta secondi se sia cambiato qualcosa.
 *
 * Due scelte che vale la pena spiegare.
 *
 * La riconnessione la gestiamo noi, non EventSource. La sua automatica ripeterebbe lo stesso
 * indirizzo, e il biglietto vale una volta sola: fallirebbe per sempre. Riconnettersi da soli
 * serve comunque, perche' e' il momento in cui bisogna anche riallinearsi.
 *
 * Un evento perso non e' un dato sbagliato. Gli eventi dicono solo *cosa* e' cambiato: chi li
 * riceve rilegge dalla sorgente. Se la connessione cade, al ritorno si annuncia "riconnesso"
 * e le schermate si ricaricano da sole.
 */

const iscritti = new Map();

let sorgente = null;
let attesaMs = 1000;
let riconnessione = null;
let acceso = false;
// Il codice della connessione, ricevuto all'apertura: con quello si dice che si chiude (vedi avvisaChiusura).
let connessione = null;
let ascoltoPagina = false;

function avvisa(tipo, dati) {
  const insieme = iscritti.get(tipo);
  if (!insieme) return;
  for (const fn of insieme) {
    try {
      fn(dati);
    } catch (e) {
      // Un iscritto che sbaglia non deve zittire gli altri.
      console.error("Errore in un iscritto al canale eventi", e);
    }
  }
}

// "whatsapp": lo stato del collegamento del numero tramite gateway, e SOLO lo stato (account,
// stato, stato della sessione). Il QR, il codice di abbinamento e la sfida della passkey non
// passano mai di qui: l'evento arriva a ogni connessione dello spazio, e la pagina del collegamento,
// quando lo riceve, chiede lei il QR al server.
// "collegati": qualcuno e' entrato o uscito. Lo ricevono solo le connessioni di chi amministra la piattaforma.
const TIPI = ["messaggio", "prenotazione", "checkin", "notifica", "tariffe", "whatsapp", "collegati"];

async function apri() {
  if (!acceso || sorgente) return;
  let biglietto;
  try {
    // skipToast: e' una chiamata di sfondo, che riprova da se' con attese crescenti. Senza, ogni
    // giro senza rete (o con il token che non si e' potuto rinnovare) era un toast in piu'.
    const { data } = await apiClient.post("/eventi/biglietto", undefined, { skipToast: true });
    biglietto = data?.data?.biglietto;
  } catch (e) {
    riprova();
    return;
  }
  if (!biglietto) {
    riprova();
    return;
  }

  const es = new EventSource(
    `${API_ROOT}/api/v1/public/eventi/canale?biglietto=${encodeURIComponent(biglietto)}`);
  sorgente = es;

  es.addEventListener("aperto", (ev) => {
    try {
      connessione = JSON.parse(ev.data)?.connessione || null;
    } catch (e) {
      connessione = null;
    }
    // Tornati in linea: l'attesa riparte da capo e chi vuole si riallinea con cio' che
    // potrebbe essere successo mentre non eravamo collegati.
    attesaMs = 1000;
    avvisa("riconnesso", {});
  });

  for (const tipo of TIPI) {
    es.addEventListener(tipo, (ev) => {
      let dati = {};
      try {
        dati = ev.data ? JSON.parse(ev.data) : {};
      } catch (e) {
        dati = {};
      }
      avvisa(tipo, dati);
    });
  }

  // Un amministratore ha chiuso le sessioni di questa persona (lo riceve solo lei): si esce subito, senza
  // aspettare il primo 401. Il server chiude comunque tutto: questo evento serve solo a non lasciare la
  // persona davanti a una pagina che non risponde piu'.
  es.addEventListener("sessione-chiusa", () => {
    keycloakService.logout("sessione_chiusa");
  });

  es.onerror = () => {
    // Si chiude e si riapre da capo: il biglietto e' gia' speso, quindi lasciare che sia
    // EventSource a ritentare significherebbe ritentare all'infinito con uno scaduto.
    es.close();
    if (sorgente === es) sorgente = null;
    avvisaChiusura();
    riprova();
  };
}

/**
 * Dice al server che questa scheda chiude la sua connessione. Da solo non se ne accorge: davanti ha il bilanciatore e
 * Cloud Run, che quando il browser se ne va tengono aperta la loro parte fino alla scadenza, e chi ha chiuso la finestra
 * resterebbe fra gli utenti collegati fino a mezz'ora. sendBeacon parte anche mentre la pagina se ne va; fetch con
 * keepalive e' il ripiego dei browser che non lo hanno. Senza gettone: lo protegge il codice, che sa solo questa scheda.
 */
function avvisaChiusura() {
  if (!connessione) return;
  const url = `${API_ROOT}/api/v1/public/eventi/chiudi?connessione=${encodeURIComponent(connessione)}`;
  connessione = null;
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url)) return;
  } catch (e) {
    // si prova con fetch
  }
  try {
    fetch(url, { method: "POST", keepalive: true }).catch(() => {});
  } catch (e) {
    // pazienza: il server se ne accorgera' alla scadenza della connessione
  }
}

/**
 * La pagina si chiude, si ricarica o cambia: la connessione se ne va con lei. Una volta per pagina.
 *
 * Tornando indietro col browser la pagina puo' rinascere intera dalla sua memoria (persisted): la connessione e' gia'
 * stata dichiarata chiusa, quindi si chiude davvero all'andata e se ne apre una nuova al ritorno.
 */
function ascoltaChiusuraPagina() {
  if (ascoltoPagina || typeof window === "undefined") return;
  ascoltoPagina = true;
  window.addEventListener("pagehide", (ev) => {
    avvisaChiusura();
    if (ev.persisted && sorgente) {
      sorgente.close();
      sorgente = null;
    }
  });
  window.addEventListener("pageshow", (ev) => {
    if (ev.persisted && acceso && !sorgente) {
      attesaMs = 1000;
      apri();
    }
  });
}

// Fino a cinque secondi in piu', a caso, su ogni attesa. Quando un'istanza del server si spegne
// (un rilascio, o il servizio che si restringe) tutte le schede attaccate a lei cadono nello
// stesso istante: con la stessa attesa si ripresenterebbero tutte insieme, ognuna con un
// biglietto e le riletture delle sue schermate. Sparse su qualche secondo, il server non le
// sente arrivare. Quello che succede in quei secondi non va perso: al ritorno le schermate rileggono.
const CASUALITA_MS = 5000;

function riprova() {
  if (!acceso || riconnessione) return;
  riconnessione = setTimeout(() => {
    riconnessione = null;
    apri();
  }, attesaMs + Math.floor(Math.random() * CASUALITA_MS));
  // Attesa crescente fino a mezzo minuto: se il server e' giu', non lo si tempesta.
  attesaMs = Math.min(attesaMs * 2, 30000);
}

export default {
  /** Apre il canale. Da chiamare una volta sola, dopo l'accesso. */
  avvia() {
    if (acceso) return;
    acceso = true;
    attesaMs = 1000;
    ascoltaChiusuraPagina();
    apri();
  },

  /** Chiude il canale e smette di riprovare. Da chiamare all'uscita. */
  ferma() {
    acceso = false;
    if (riconnessione) {
      clearTimeout(riconnessione);
      riconnessione = null;
    }
    if (sorgente) {
      sorgente.close();
      sorgente = null;
    }
    avvisaChiusura();
  },

  /**
   * Si iscrive a un tipo di evento. Torna la funzione per disiscriversi, da chiamare
   * quando la schermata si smonta.
   *
   * Il tipo "riconnesso" non e' un evento del server: e' l'avviso che il canale e' tornato
   * dopo una caduta, ed e' il momento di rileggere quello che si mostra.
   */
  su(tipo, fn) {
    if (!iscritti.has(tipo)) iscritti.set(tipo, new Set());
    iscritti.get(tipo).add(fn);
    return () => iscritti.get(tipo)?.delete(fn);
  },

  /** Se il canale e' aperto adesso: serve alle schermate per decidere se serve un ripiego. */
  attivo() {
    return !!sorgente && sorgente.readyState === 1;
  },

  /**
   * Consegna un evento agli iscritti SENZA che arrivi dal server, con gli stessi dati che
   * manderebbe il canale.
   *
   * Serve all'app: quando arriva una notifica push mentre si guarda proprio la pagina che la
   * riguarda, non si mostra un avviso doppione, ma la pagina deve comunque ricaricarsi. Non tutte
   * le novita' passano da questo canale (i messaggi recuperati dal riallineamento con Channex non
   * lo annunciano, le pulizie non hanno un tipo), e la push e' l'unico segnale che c'e'.
   * Ricaricare due volte per la stessa novita' non costa niente: le schermate rileggono in
   * silenzio e ridisegnano solo se qualcosa e' cambiato. Nel sito non lo chiama nessuno.
   */
  annuncia(tipo, dati) {
    avvisa(tipo, dati || {});
  },
};
